import { isPveMode, type GameMode, type PlayerStats, type StatKey, type WeaponId } from './types';

export type UnlockKind = 'race' | 'body' | 'outfit' | 'trail' | 'perk';
/** In Locker tab order: the creature first, then what it wears. */
export const UNLOCK_KINDS: readonly UnlockKind[] = ['race', 'body', 'outfit', 'trail', 'perk'];

export type Requirement =
  | { type: 'default' }
  | { type: 'level'; level: number }
  | { type: 'stat'; stat: StatKey; value: number }
  | { type: 'stars'; stars: number }
  | { type: 'all'; of: Requirement[] };

/** What a gameplay perk changes. The simulation reads these; Versus ignores them entirely. */
export interface PerkEffect {
  maxHpBonus?: number;
  fallDamageMultiplier?: number;
  reviveOnce?: boolean;
  extraAmmo?: Partial<Record<WeaponId, number>>;
  grantWeapon?: { weapon: WeaponId; ammo: number };
  clusterGrenade?: boolean;
  /** Multiplies how far the boot sends an enemy. */
  bootMultiplier?: number;
  /** Multiplies throw speed and explosion knockback. */
  throwMultiplier?: number;
  /** RPGs per match that curve toward the nearest enemy. */
  homingRockets?: number;
}

export interface UnlockDef {
  id: string;
  kind: UnlockKind;
  name: string;
  description: string;
  requirement: Requirement;
  /** Co-op Siege and Solo Campaign only. Every perk sets this. */
  pveOnly?: boolean;
  effect?: PerkEffect;
}

const lvl = (level: number): Requirement => ({ type: 'level', level });
const stat = (s: StatKey, value: number): Requirement => ({ type: 'stat', stat: s, value });
const stars = (n: number): Requirement => ({ type: 'stars', stars: n });
const all = (...of: Requirement[]): Requirement => ({ type: 'all', of });
const DEFAULT: Requirement = { type: 'default' };

export const UNLOCKS: readonly UnlockDef[] = [
  // Races: the creature that wears everything else. Purely visual; every race shares the mole's hitbox and stats.
  { id: 'race.mole', kind: 'race', name: 'Mole', description: 'Velvet fur and a nose for digging.', requirement: DEFAULT },
  { id: 'race.rat', kind: 'race', name: 'Rat', description: 'Long snout, longer tail, no manners.', requirement: lvl(3) },
  { id: 'race.rabbit', kind: 'race', name: 'Rabbit', description: 'Long ears, buck teeth, a fluffy pom.', requirement: lvl(7) },
  { id: 'race.hedgehog', kind: 'race', name: 'Hedgehog', description: 'Prickly on top, soft underneath.', requirement: lvl(13) },
  { id: 'race.badger', kind: 'race', name: 'Badger', description: 'A stripe down the face and a grudge.', requirement: lvl(19) },

  // Body types — purely visual; every body shares the same hitbox.
  { id: 'body.lean', kind: 'body', name: 'Lean', description: 'The standard-issue build.', requirement: DEFAULT },
  { id: 'body.stocky', kind: 'body', name: 'Stocky', description: 'Broad shoulders, short legs.', requirement: lvl(3) },
  { id: 'body.round', kind: 'body', name: 'Round', description: 'Mostly belly.', requirement: lvl(8) },
  { id: 'body.lanky', kind: 'body', name: 'Lanky', description: 'Tall and wobbly.', requirement: lvl(14) },
  { id: 'body.tiny', kind: 'body', name: 'Tiny', description: 'Same hitbox, half the mole.', requirement: lvl(22) },
  { id: 'body.mega', kind: 'body', name: 'Mega', description: 'A mountain with a snout.', requirement: lvl(35) },

  // Outfits
  { id: 'outfit.none', kind: 'outfit', name: 'Au naturel', description: 'Just fur.', requirement: DEFAULT },
  { id: 'outfit.bandana', kind: 'outfit', name: 'Bandana', description: 'Red and ready.', requirement: lvl(2) },
  { id: 'outfit.hardhat', kind: 'outfit', name: 'Hard hat', description: 'Site safety first.', requirement: lvl(4) },
  { id: 'outfit.scarf', kind: 'outfit', name: 'Scarf', description: 'For windy maps.', requirement: lvl(6) },
  { id: 'outfit.goggles', kind: 'outfit', name: 'Goggles', description: 'Keeps the dirt out.', requirement: lvl(9) },
  { id: 'outfit.tophat', kind: 'outfit', name: 'Top hat', description: 'Distinguished digging.', requirement: lvl(12) },
  { id: 'outfit.hoodie', kind: 'outfit', name: 'Hoodie', description: 'Comfy and anonymous.', requirement: lvl(16) },
  { id: 'outfit.armor', kind: 'outfit', name: 'Plate armour', description: 'Looks heavy. Is not.', requirement: lvl(20) },
  { id: 'outfit.crown', kind: 'outfit', name: 'Crown', description: 'Earned, not given.', requirement: all(lvl(30), stat('wins', 50)) },
  { id: 'outfit.astronaut', kind: 'outfit', name: 'Astronaut', description: 'Dug too deep, came out the other side.', requirement: lvl(40) },
  { id: 'outfit.headhunter', kind: 'outfit', name: 'Headhunter visor', description: 'For 100 headshots.', requirement: stat('headshots', 100) },
  { id: 'outfit.sharpshooter', kind: 'outfit', name: 'Sharpshooter sash', description: 'For 50 long shots.', requirement: stat('longShots', 50) },
  { id: 'outfit.laurels', kind: 'outfit', name: 'Laurels', description: 'For 10 flawless wins.', requirement: stat('flawlessWins', 10) },

  // Trails — follow thrown things and flying moles.
  { id: 'trail.none', kind: 'trail', name: 'No trail', description: 'Clean shots.', requirement: DEFAULT },
  { id: 'trail.dust', kind: 'trail', name: 'Dust', description: 'A puff of dirt.', requirement: lvl(5) },
  { id: 'trail.sparks', kind: 'trail', name: 'Sparks', description: 'Crackling embers.', requirement: lvl(10) },
  { id: 'trail.rainbow', kind: 'trail', name: 'Rainbow', description: 'Seven colours of doom.', requirement: lvl(18) },
  { id: 'trail.fire', kind: 'trail', name: 'Fire', description: 'Burning arc.', requirement: lvl(25) },
  { id: 'trail.glitch', kind: 'trail', name: 'Glitch', description: 'Stutters through the sky.', requirement: lvl(33) },
  { id: 'trail.comet', kind: 'trail', name: 'Comet', description: 'Ice-blue tail.', requirement: lvl(45) },
  { id: 'trail.tsunami', kind: 'trail', name: 'Tsunami', description: 'For 25 splash kills.', requirement: stat('drownKills', 25) },

  // Gameplay perks — Co-op & Campaign only.
  {
    id: 'perk.sturdy_boots', kind: 'perk', name: 'Sturdy boots', description: 'Half fall damage.',
    requirement: lvl(7), pveOnly: true, effect: { fallDamageMultiplier: 0.5 },
  },
  {
    id: 'perk.cluster_grenade', kind: 'perk', name: 'Cluster grenade', description: 'Grenades split into three on the first bounce.',
    requirement: lvl(11), pveOnly: true, effect: { clusterGrenade: true },
  },
  {
    id: 'perk.rocket_pack', kind: 'perk', name: 'RPG pack', description: 'Two extra RPGs per match: fast, flat, and loud.',
    requirement: lvl(15), pveOnly: true, effect: { grantWeapon: { weapon: 'rpg', ammo: 2 } },
  },
  {
    id: 'perk.big_boots', kind: 'perk', name: 'Big boots', description: 'The boot sends them half again as far.',
    requirement: lvl(19), pveOnly: true, effect: { bootMultiplier: 1.5 },
  },
  {
    id: 'perk.thick_fur', kind: 'perk', name: 'Thick fur', description: '+10 max HP on every mole.',
    requirement: stars(3), pveOnly: true, effect: { maxHpBonus: 10 },
  },
  {
    id: 'perk.second_wind', kind: 'perk', name: 'Second wind', description: 'Your first fallen mole comes back once with 20 HP.',
    requirement: stars(9), pveOnly: true, effect: { reviveOnce: true },
  },
  {
    id: 'perk.iron_arm', kind: 'perk', name: 'Iron arm', description: 'Throws 15% harder; blasts shove 15% farther.',
    requirement: stars(15), pveOnly: true, effect: { throwMultiplier: 1.15 },
  },
  {
    id: 'perk.homing_rocket', kind: 'perk', name: 'Homing RPG', description: 'One extra RPG per match that curves toward the nearest enemy.',
    requirement: stars(24), pveOnly: true, effect: { extraAmmo: { rpg: 1 }, homingRockets: 1 },
  },
];

export const UNLOCKS_BY_ID: ReadonlyMap<string, UnlockDef> = new Map(UNLOCKS.map((u) => [u.id, u]));

export function getUnlock(id: string): UnlockDef | undefined {
  return UNLOCKS_BY_ID.get(id);
}

export function unlocksOfKind(kind: UnlockKind): UnlockDef[] {
  return UNLOCKS.filter((u) => u.kind === kind);
}

/** Every race id, the default first. Race is cosmetic: the simulation never reads it. */
export const RACE_IDS: readonly string[] = unlocksOfKind('race').map((u) => u.id);
export const DEFAULT_RACE = 'race.mole';

export const DEFAULT_EQUIPPED = { race: DEFAULT_RACE, body: 'body.lean', outfit: 'outfit.none', trail: 'trail.none' } as const;

/** The race a loadout wears: the mole when it names none (profiles saved before races) or one we do not know. */
export function raceOf(equipped: { race?: string }): string {
  const id = equipped.race;
  return id !== undefined && RACE_IDS.includes(id) ? id : DEFAULT_RACE;
}

/**
 * A race for a computer-controlled mole (a co-op raider), picked from a stable key such as its unit
 * id. The same key gives the same race on every client, so a crowd of raiders looks varied while the
 * simulation stays out of it.
 */
export function raceForKey(key: string): string {
  // FNV-1a, then a murmur-style finish so keys that differ in one character spread over all races.
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return RACE_IDS[(h >>> 0) % RACE_IDS.length] ?? DEFAULT_RACE;
}

/** What the requirement checks are evaluated against. */
export interface UnlockContext {
  level: number;
  stats: PlayerStats;
  /** Total campaign stars. */
  stars: number;
}

export function meetsRequirement(req: Requirement, ctx: UnlockContext): boolean {
  switch (req.type) {
    case 'default':
      return true;
    case 'level':
      return ctx.level >= req.level;
    case 'stat':
      return (ctx.stats[req.stat] ?? 0) >= req.value;
    case 'stars':
      return ctx.stars >= req.stars;
    case 'all':
      return req.of.every((r) => meetsRequirement(r, ctx));
  }
}

export function isUnlocked(def: UnlockDef, ctx: UnlockContext): boolean {
  return meetsRequirement(def.requirement, ctx);
}

export function unlockedIds(ctx: UnlockContext): string[] {
  return UNLOCKS.filter((u) => isUnlocked(u, ctx)).map((u) => u.id);
}

/** Perks are refused outside Co-op / Campaign. Cosmetics are always usable. */
export function isUsableInMode(def: UnlockDef, mode: GameMode): boolean {
  return !def.pveOnly || isPveMode(mode);
}

const STAT_LABELS: Record<StatKey, string> = {
  matches: 'matches',
  wins: 'wins',
  flawlessWins: 'flawless wins',
  kills: 'kills',
  hits: 'hits',
  headshots: 'headshots',
  directHits: 'direct hits',
  longShots: 'long shots',
  drownKills: 'splash kills',
};

/** Short human label for a lock badge: "Lv 12", "100 headshots", "Lv 30 · 50 wins". */
export function requirementLabel(req: Requirement): string {
  switch (req.type) {
    case 'default':
      return 'Unlocked';
    case 'level':
      return `Lv ${req.level}`;
    case 'stat':
      return `${req.value} ${STAT_LABELS[req.stat]}`;
    case 'stars':
      return `${req.stars} campaign star${req.stars === 1 ? '' : 's'}`;
    case 'all':
      return req.of.map(requirementLabel).join(' · ');
  }
}

/** Sum perk effects so the simulation reads one object. */
export function combinePerkEffects(perks: readonly UnlockDef[]): PerkEffect {
  const out: PerkEffect = {};
  for (const p of perks) {
    const e = p.effect;
    if (!e) continue;
    if (e.maxHpBonus) out.maxHpBonus = (out.maxHpBonus ?? 0) + e.maxHpBonus;
    if (e.fallDamageMultiplier !== undefined) {
      out.fallDamageMultiplier = (out.fallDamageMultiplier ?? 1) * e.fallDamageMultiplier;
    }
    if (e.bootMultiplier !== undefined) out.bootMultiplier = (out.bootMultiplier ?? 1) * e.bootMultiplier;
    if (e.throwMultiplier !== undefined) out.throwMultiplier = (out.throwMultiplier ?? 1) * e.throwMultiplier;
    if (e.homingRockets) out.homingRockets = (out.homingRockets ?? 0) + e.homingRockets;
    if (e.reviveOnce) out.reviveOnce = true;
    if (e.clusterGrenade) out.clusterGrenade = true;
    if (e.extraAmmo) {
      out.extraAmmo = { ...out.extraAmmo };
      for (const [w, n] of Object.entries(e.extraAmmo) as [WeaponId, number][]) {
        out.extraAmmo[w] = (out.extraAmmo[w] ?? 0) + n;
      }
    }
    if (e.grantWeapon) {
      out.extraAmmo = { ...out.extraAmmo };
      out.extraAmmo[e.grantWeapon.weapon] = (out.extraAmmo[e.grantWeapon.weapon] ?? 0) + e.grantWeapon.ammo;
    }
  }
  return out;
}
